import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Users, Info } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '@/components/SEO';
import { format, parse } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Calendar as CalendarIcon } from 'lucide-react';

// Data definitions outside component to keep it clean
const allActivities = [
    // Zimbabwe
    { id: 'zw1', name: 'Bungee Jumping (Solo)', price: 180, childPrice: 180, place: 'Zimbabwe' },
    { id: 'zw2', name: 'Bridge Swing (Solo)', price: 180, childPrice: 180, place: 'Zimbabwe' },
    { id: 'zw3', name: 'Gorge Swing (Solo)', price: 115, childPrice: 115, place: 'Zimbabwe' },
    { id: 'zw4', name: 'Big Air Experience', price: 265, childPrice: 265, place: 'Zimbabwe' },
    { id: 'zw5', name: 'Zip Line (Solo)', price: 95, childPrice: 95, place: 'Zimbabwe' },
    { id: 'zw6', name: 'Bridge Slide (Solo)', price: 55, childPrice: 55, place: 'Zimbabwe' },
    { id: 'zw7', name: 'Flight of Angels (12-13 Min)', price: 165, childPrice: 165, place: 'Zimbabwe' },
    { id: 'zw8', name: 'Zambezi Spectacular (25 Min)', price: 315, childPrice: 315, place: 'Zimbabwe' },
    { id: 'zw9', name: 'White Water Rafting (Full Day)', price: 140, childPrice: 140, place: 'Zimbabwe' },
    { id: 'zw10', name: 'Adventure Jetboat', price: 120, childPrice: 60, place: 'Zimbabwe' },
    { id: 'zw11', name: 'The Riversong Luxury Cruise', price: 90, childPrice: 45, place: 'Zimbabwe' },
    { id: 'zw12', name: 'Hwange National Park Day Trip', price: 320, childPrice: 160, place: 'Zimbabwe' },
    { id: 'zw13', name: 'Through The Eyes Of An Elephant', price: 130, childPrice: 65, place: 'Zimbabwe' },
    { id: 'zw14', name: 'Simunye: Spirit of Africa', price: 40, childPrice: 20, place: 'Zimbabwe' },
    { id: 'zw15', name: 'Bushtracks Express Dinner Safari', price: 240, childPrice: 120, place: 'Zimbabwe' },
    { id: 'zw16', name: 'Historical Bridge Tour', price: 70, childPrice: 35, place: 'Zimbabwe' },
    { id: 'zw17', name: 'Village Visit', price: 65, childPrice: 33, place: 'Zimbabwe' },

    // Zambia
    { id: 'zm1', name: 'Royal Livingstone Express Dinner', price: 220, childPrice: 110, place: 'Zambia' },
    { id: 'zm2', name: 'Microlight Flight (15 Min)', price: 196, childPrice: 196, place: 'Zambia' },
    { id: 'zm3', name: 'Zambia Helicopter Flight (15 Min)', price: 211, childPrice: 211, place: 'Zambia' },
    { id: 'zm4', name: 'Livingstone Island Lunch', price: 209, childPrice: 209, place: 'Zambia' },
    { id: 'zm5', name: 'Livingstone Island Morning Breezer', price: 159, childPrice: 159, place: 'Zambia' },
    { id: 'zm13', name: 'Livingstone Island Afternoon Tea', price: 199, childPrice: 199, place: 'Zambia' },
    { id: 'zm14', name: 'Livingstone Island Walks', price: 100, childPrice: 100, place: 'Zambia' },
    { id: 'zm6', name: 'The Elephant Cafe (Speedboat)', price: 260, childPrice: 130, place: 'Zambia' },
    { id: 'zm7', name: 'White Rhino Walking Safari', price: 144, childPrice: 144, place: 'Zambia' },
    { id: 'zm8', name: 'Quad Biking - Eco Trail', price: 90, childPrice: 90, place: 'Zambia' },
    { id: 'zm9', name: 'African Queen Sunset Cruise', price: 75, childPrice: 38, place: 'Zambia' },
    { id: 'zm10', name: 'Victoria Falls Guided Tour', price: 34, childPrice: 17, place: 'Zambia' },
    { id: 'zm11', name: 'Historic Bridge Tour', price: 55, childPrice: 28, place: 'Zambia' },
    { id: 'zm12', name: 'Zambezi River Float', price: 130, childPrice: 65, place: 'Zambia' },

    // Botswana
    { id: 'bw1', name: 'Chobe Day Trip (Full Day)', price: 198, childPrice: 99, place: 'Botswana' },
    { id: 'bw2', name: 'Chobe River Cruise', price: 53, childPrice: 27, place: 'Botswana' },
    { id: 'bw3', name: 'Chobe Game Drive', price: 53, childPrice: 27, place: 'Botswana' },
    { id: 'bw4', name: 'Mokoro Excursion', price: 75, childPrice: 38, place: 'Botswana' },
    { id: 'bw5', name: 'Khwai Nature Walk', price: 60, childPrice: 30, place: 'Botswana' },
    { id: 'bw6', name: 'Khwai Night Drive', price: 95, childPrice: 48, place: 'Botswana' },
];

const allAccommodations = [
    // Zimbabwe
    { id: 'acc1', name: 'The Victoria Falls Hotel', price: 450, place: 'Zimbabwe', image: '/images/vic-falls-hotel.png' },
    { id: 'acc2', name: 'Elephant Camp', price: 800, place: 'Zimbabwe', image: '/images/elephant-camp-sunset.jpg' },
    { id: 'acc3', name: 'Shearwater Explorers Village', price: 120, place: 'Zimbabwe', image: '/images/311229435.jpg' },
    { id: 'acc6', name: 'Hwange Safari Lodge', price: 250, place: 'Zimbabwe', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc7', name: 'Somalisa Camp', price: 950, place: 'Zimbabwe', image: 'https://africanbushcamps.com/wp-content/uploads/2018/05/Somalisa_450-1024x683.jpg' },

    // Zambia
    { id: 'acc4', name: 'Royal Livingstone Hotel', price: 650, place: 'Zambia', image: 'https://assets.anantara.com/image/upload/q_auto,f_auto,c_limit,w_1920/media/minor/anantara/images/royal-livingstone-by-anantara/gallery/overview/royal_livingstone_ext_lounge_grounds_dusk_1920x1037.jpg' },
    { id: 'acc5', name: 'Avani Victoria Falls Resort', price: 300, place: 'Zambia', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80' },

    // Botswana
    { id: 'acc8', name: 'Chobe Safari Lodge', price: 280, place: 'Botswana', image: 'https://www.chobesafarilodge.com/images/slideshow/chobe-safari-lodge-pool-2025-2.jpg' },
    { id: 'acc9', name: 'Chobe Game Lodge', price: 600, place: 'Botswana', image: '/images/Chobe-Game-Lodge-Swimming-Pool-and-Restaurant.jpg' },
    { id: 'acc14', name: 'Muchenje Safari Lodge', price: 585, place: 'Botswana', image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc15', name: 'Tlouwana Camp', price: 472, place: 'Botswana', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc10', name: 'Khwai Expeditions Camp', price: 455, place: 'Botswana', image: '/images/khwai-camp.png' },
    { id: 'acc11', name: 'Nata Lodge', price: 150, place: 'Botswana', image: '/images/nata-lodge.png' },
    { id: 'acc16', name: 'Setari Camp', price: 480, place: 'Botswana', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc17', name: 'Kala Camp', price: 480, place: 'Botswana', image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc18', name: 'Kala Treehouse', price: 7000, place: 'Botswana', image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1000&q=80' },

    // Kenya
    { id: 'acc12', name: 'Angama Mara', price: 1200, place: 'Kenya', image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc13', name: 'Keekorok Lodge', price: 350, place: 'Kenya', image: '/images/a-57.jpg' },
    { id: 'acc19', name: 'Royal Mara Safari Lodge', price: 684, place: 'Kenya', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc20', name: 'Mara Intrepids Camp', price: 550, place: 'Kenya', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc21', name: 'Mara Elatia', price: 550, place: 'Kenya', image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc22', name: 'Samburu Intrepids', price: 495, place: 'Kenya', image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc23', name: 'Great Rift Valley Lodge', price: 365, place: 'Kenya', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc24', name: 'Kizingo Eco Lodge', price: 315, place: 'Kenya', image: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc25', name: 'Voyager Ziwani Camp', price: 175, place: 'Kenya', image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80' },
    { id: 'acc26', name: 'Voyager Beach Resort', price: 160, place: 'Kenya', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80' },
];

const allTransfers = [
    { id: 'tr1', name: 'Victoria Falls Airport Transfer', price: 15, childPrice: 15, place: 'Zimbabwe' },
    { id: 'tr2', name: 'Livingstone Airport Transfer', price: 20, childPrice: 20, place: 'Zambia' },
    { id: 'tr3', name: 'Kasane Airport Transfer', price: 25, childPrice: 25, place: 'Botswana' },
    { id: 'tr4', name: 'Vic Falls ↔ Livingstone (Cross-Border)', price: 30, childPrice: 30, place: 'Zim-Zam' },
    { id: 'tr5', name: 'Vic Falls ↔ Kasane/Chobe', price: 50, childPrice: 50, place: 'Zim-Bots' },
    { id: 'tr6', name: 'Vic Falls ↔ Hwange National Park', price: 120, childPrice: 120, place: 'Zimbabwe' },
];

const BookingPage = () => {
    const [cart, setCart] = useState([]);
    // State to track inputs for each item before they are added to cart
    // Format: { [itemId]: { date: '', adults: 1, children: 0 } }
    const [itemInputs, setItemInputs] = useState({});

    // Contact form state
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const location = useLocation();

    // Helper to get input state for an item safely
    const getItemInput = (id, defaultPrice) => {
        return itemInputs[id] || { date: '', adults: 1, children: 0 };
    };

    // Handle input changes for a specific item card
    const handleInputChange = (id, field, value) => {
        setItemInputs(prev => ({
            ...prev,
            [id]: {
                ...getItemInput(id),
                [field]: value
            }
        }));
    };

    // Initialize from navigation state (e.g. "Book Now" from another page)
    useEffect(() => {
        try {
            if (location && location.state && location.state.bookItem) {
                const itemName = location.state.bookItem;
                const item = [...allActivities, ...allAccommodations, ...allTransfers].find(i => i.name === itemName);

                if (item) {
                    // Initialize its inputs if not set
                    if (!itemInputs[item.id]) {
                        setItemInputs(prev => ({
                            ...prev,
                            [item.id]: { date: '', adults: 1, children: 0 }
                        }));
                    }
                    // Wait for render then scroll
                    setTimeout(() => {
                        const element = document.getElementById(`item-${item.id}`);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            element.classList.add('ring-2', 'ring-primary', 'shadow-lg');
                            setTimeout(() => element.classList.remove('ring-2', 'ring-primary', 'shadow-lg'), 2000);
                        }
                    }, 100);
                }
            }
        } catch (error) {
            console.error("Error handling location state:", error);
        }
    }, [location]);

    const addToCart = (item) => {
        const inputs = getItemInput(item.id);

        // Validation: Must have date and at least 1 adult
        if (!inputs.date) {
            alert("Please select a date first.");
            return;
        }
        if (inputs.adults < 1) {
            alert("Please include at least 1 person.");
            return;
        }

        // Check if already in cart
        if (cart.find(i => i.id === item.id)) {
            alert("This item is already in your itinerary.");
            return;
        }

        setCart([...cart, { ...item, ...inputs }]);
    };

    const removeFromCart = (id) => setCart(cart.filter(i => i.id !== id));

    const total = cart.reduce((sum, item) => {
        const itemPrice = Number(item.price) || 0;
        const itemChildPrice = Number(item.childPrice) || 0;
        return sum + (itemPrice * item.adults) + (itemChildPrice * item.children);
    }, 0);

    const groupedActivities = {
        'Zimbabwe': allActivities.filter(a => a.place === 'Zimbabwe'),
        'Zambia': allActivities.filter(a => a.place === 'Zambia'),
        'Botswana': allActivities.filter(a => a.place === 'Botswana'),
    };

    const getEmailRecipient = () => {
        // Mixed cart logic
        const hasActivitiesOrTransfers = cart.some(item =>
            allActivities.some(a => a.id === item.id) || allTransfers.some(t => t.id === item.id)
        );
        const hasAccommodation = cart.some(item => allAccommodations.some(a => a.id === item.id));

        if (hasActivitiesOrTransfers && hasAccommodation) {
            return "reservations@peregrinetoursandsafaris.com,bookings@peregrinetoursandsafaris.com";
        } else if (hasAccommodation) {
            return "bookings@peregrinetoursandsafaris.com";
        } else {
            return "reservations@peregrinetoursandsafaris.com";
        }
    };

    const handleSendInquiry = () => {
        if (cart.length === 0) {
            alert("Your itinerary is empty. Please add some items.");
            return;
        }
        if (!contactForm.name || !contactForm.email) {
            alert("Please fill in your name and email.");
            return;
        }

        const emailRecipient = getEmailRecipient();
        const itemsList = cart.map(item => {
            let itemTotal;
            let itemQuantity;
            if (item.id.startsWith('acc')) {
                itemTotal = (Number(item.price) || 0) * item.adults;
                itemQuantity = `${item.adults} Nights, ${item.children} Guests`;
            } else if (item.id.startsWith('tr')) {
                itemTotal = (Number(item.price) || 0) * item.adults;
                itemQuantity = `${item.adults} Pax`;
            } else {
                itemTotal = (Number(item.price) || 0) * item.adults + (Number(item.childPrice) || 0) * item.children;
                itemQuantity = `${item.adults} Adults, ${item.children} Kids`;
            }
            return `- ${item.name}\n  Date: ${item.date || 'TBD'}\n  Details: ${itemQuantity}\n  Price: $${itemTotal}`;
        }).join('\n\n');

        const body = `Name: ${contactForm.name}
Email: ${contactForm.email}
Phone: ${contactForm.phone}

Message:
${contactForm.message}

--- PROPOSED ITINERARY ---
${itemsList}

Total Estimate: $${total}

(Generated by pereginetoursandsafaris.com)`;

        window.location.href = `mailto:${emailRecipient}?subject=Itinerary Inquiry from ${contactForm.name}&body=${encodeURIComponent(body)}`;
    };

    // Component for rendering a single item card
    const ItemCard = ({ item, type }) => {
        const inputs = getItemInput(item.id);
        const isInCart = cart.find(i => i.id === item.id);
        const labels = type === 'acc'
            ? { date: 'Check In', qty: 'Nights', sub: 'Guests' }
            : type === 'tr'
                ? { date: 'Travel Date', qty: 'Pax', sub: '' }
                : { date: 'Date', qty: 'Adults', sub: 'Kids' };

        return (
            <Card id={`item-${item.id}`} className={`p-4 transition-all duration-300 ${isInCart ? 'ring-2 ring-primary border-primary bg-primary/5' : 'hover:shadow-md'}`}>
                <div className="flex flex-col gap-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <h4 className="font-bold text-foreground leading-tight">{item.name}</h4>
                            <p className="text-primary font-bold text-sm mt-1">
                                {type === 'acc' ? `From $${item.price} /night` : `$${item.price}`}
                            </p>
                        </div>
                        {isInCart && (
                            <span className="bg-primary text-primary-foreground text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                                Added
                            </span>
                        )}
                    </div>

                    {/* Input Selection Area - Always visible to allow configuring before adding */}
                    <div className="grid grid-cols-12 gap-2 bg-slate-50 p-2 rounded-md border border-slate-100">
                        <div className="col-span-5 space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{labels.date}</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full h-8 text-xs justify-start bg-white border-input font-normal hover:bg-slate-50",
                                            !inputs.date && "text-muted-foreground"
                                        )}
                                        disabled={isInCart}
                                    >
                                        <CalendarIcon className="mr-2 h-3 w-3" />
                                        {inputs.date ? format(parse(inputs.date, 'yyyy-MM-dd', new Date()), "PPP") : <span>Pick a date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={inputs.date ? parse(inputs.date, 'yyyy-MM-dd', new Date()) : undefined}
                                        onSelect={(date) => handleInputChange(item.id, 'date', date ? format(date, 'yyyy-MM-dd') : '')}
                                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className={`space-y-1 ${labels.sub ? 'col-span-3' : 'col-span-4'}`}>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{labels.qty}</label>
                            <Input
                                type="number"
                                min="1"
                                value={inputs.adults}
                                onChange={(e) => handleInputChange(item.id, 'adults', parseInt(e.target.value) || 0)}
                                className="h-8 text-xs bg-white"
                                disabled={isInCart}
                            />
                        </div>
                        {labels.sub && (
                            <div className="col-span-4 space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{labels.sub}</label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={inputs.children}
                                    onChange={(e) => handleInputChange(item.id, 'children', parseInt(e.target.value) || 0)}
                                    className="h-8 text-xs bg-white"
                                    disabled={isInCart}
                                />
                            </div>
                        )}
                    </div>

                    {isInCart ? (
                        <Button variant="outline" size="sm" onClick={() => removeFromCart(item.id)} className="w-full text-destructive border-destructive/20 hover:bg-destructive/10">
                            Remove
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={() => addToCart(item)}
                            className="w-full bg-primary hover:bg-primary-dark"
                            disabled={!inputs.date}
                        >
                            {!inputs.date ? 'Select Date' : 'Add to Itinerary'}
                        </Button>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <SEO
                title="Itinerary Planner"
                description="Plan your safari adventure. Select activities and luxury accommodation."
                canonicalUrl="https://www.peregrinetoursandsafaris.com/booking"
            />
            <Navbar />
            <main className="container-custom pt-32 pb-20">
                <div className="flex flex-col lg:flex-row gap-8 items-start">

                    {/* LEFT COLUMN: Selection Area */}
                    <div className="flex-1 w-full order-2 lg:order-1">
                        <h1 className="text-3xl font-bold font-serif mb-6 text-foreground">Select Experiences</h1>

                        {/* Activities List */}
                        <div className="mb-12">
                            <h2 className="text-xl font-bold border-b pb-2 mb-6">Activities</h2>
                            {Object.entries(groupedActivities).map(([country, activities]) => (
                                <div key={country} className="mb-8">
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-4 sticky top-20 bg-slate-50 py-2 z-10">{country}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {activities.map(act => <ItemCard key={act.id} item={act} type="act" />)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Accommodations List */}
                        <div className="mb-12">
                            <h2 className="text-xl font-bold border-b pb-2 mb-6">Accommodation</h2>
                            {['Zimbabwe', 'Zambia', 'Botswana', 'Kenya'].map(country => {
                                const list = allAccommodations.filter(a => a.place === country);
                                if (!list.length) return null;
                                return (
                                    <div key={country} className="mb-8">
                                        <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-4 sticky top-20 bg-slate-50 py-2 z-10">{country}</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {list.map(acc => <ItemCard key={acc.id} item={acc} type="acc" />)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Transfers List */}
                        <div className="mb-12">
                            <h2 className="text-xl font-bold border-b pb-2 mb-6">Transfers</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {allTransfers.map(tr => <ItemCard key={tr.id} item={tr} type="tr" />)}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Summary & Inquiry Form */}
                    <div className="w-full lg:w-[450px] sticky top-24 order-1 lg:order-2 z-20">
                        <Card className="bg-white shadow-xl border-t-4 border-primary overflow-hidden">
                            <div className="p-6 bg-slate-900 text-white">
                                <h2 className="text-2xl font-serif font-bold mb-2">Your Itinerary</h2>
                                <p className="text-slate-400 text-sm">Review your selection and request a quote.</p>
                            </div>

                            {/* Selected Items List */}
                            <div className="p-6 bg-slate-50 min-h-[150px] max-h-[350px] overflow-y-auto custom-scrollbar border-b border-border">
                                {cart.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-slate-400 py-8">
                                        <Info className="w-8 h-8 mb-2 opacity-50" />
                                        <p className="text-sm text-center">No items added yet.<br />Select experiences from the list.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {cart.map((item, idx) => (
                                            <div key={`${item.id}-${idx}`} className="bg-white p-3 rounded border border-slate-200 shadow-sm relative group">
                                                <button
                                                    onClick={() => removeFromCart(item.id)}
                                                    className="absolute top-2 right-2 text-slate-300 hover:text-destructive transition-colors"
                                                    title="Remove item"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                                <h4 className="font-bold text-sm pr-6 leading-tight mb-1">{item.name}</h4>
                                                <div className="text-xs text-slate-500 space-y-0.5">
                                                    <p><span className="font-bold">Date:</span> {item.date}</p>
                                                    <p>
                                                        {item.id.startsWith('acc')
                                                            ? `${item.adults} Nights, ${item.children} Guests`
                                                            : `${item.adults} Adults, ${item.children} Kids`}
                                                    </p>
                                                </div>
                                                <div className="mt-2 text-primary font-bold text-sm text-right">
                                                    ${(item.price * item.adults) + ((item.childPrice || 0) * item.children)}
                                                </div>
                                            </div>
                                        ))}

                                        <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
                                            <span className="font-bold text-slate-600">Total Estimate</span>
                                            <span className="font-black text-2xl text-primary">${total}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Contact Form Details */}
                            <div className="p-6 space-y-4">
                                <h3 className="font-bold mb-4 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-primary" />
                                    Contact Details
                                </h3>
                                <div className="space-y-3">
                                    <Input
                                        placeholder="Full Name *"
                                        value={contactForm.name}
                                        onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                                        className="bg-white"
                                    />
                                    <Input
                                        placeholder="Email Address *"
                                        type="email"
                                        value={contactForm.email}
                                        onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                                        className="bg-white"
                                    />
                                    <Input
                                        placeholder="Phone Number (Optional)"
                                        type="tel"
                                        value={contactForm.phone}
                                        onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                                        className="bg-white"
                                    />
                                    <textarea
                                        className="w-full min-h-[80px] rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                        placeholder="Additional Requests or Questions..."
                                        value={contactForm.message}
                                        onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                                    />
                                </div>

                                <Button
                                    className="w-full h-12 text-lg font-bold shadow-lg mt-2"
                                    onClick={handleSendInquiry}
                                    disabled={cart.length === 0}
                                >
                                    Send Inquiry
                                </Button>
                                <p className="text-[10px] text-center text-slate-400 mt-2">
                                    Clicking send will open your email client pre-filled with this itinerary.
                                </p>
                            </div>
                        </Card>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default BookingPage;